/**
 * DsgvoSection — Berater-Cockpit-Sektion für DSGVO-Anfragen.
 *
 * Drei Tabs:
 *   1. „Offen"        — status='open', sortiert nach legal_deadline_at ASC
 *   2. „In Bearbeitung" — status='in_progress'
 *   3. „Verlauf"      — status IN ('fulfilled','rejected','expired')
 *
 * Pro Eintrag (siehe DsgvoRequestCard):
 *   - Type-Badge farblich (sky/red/amber/stone)
 *   - Frist-Countdown (rot wenn < 7 Tage, ABGELAUFEN-Box wenn überschritten)
 *   - Begründung des Mandanten (falls vorhanden)
 *   - Type-spezifische Action-Buttons:
 *       • data_export: Download JSON + „erfüllt markieren"
 *       • deletion:    „Löschung bestätigen" (Confirm-Modal) | „Mit Begründung ablehnen"
 *       • consent_withdrawal: „Widerruf bestätigen"
 *       • rectification: „Erfüllen" / „Ablehnen mit Begründung"
 *
 * Demo-Mode: nutzt DEMO_DSGVO_REQUESTS aus demoFixtures, alle Aktionen sind no-ops.
 *
 * WCAG: role=tablist + aria-selected; Modal mit useFocusTrap + ESC.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, Trash2, X } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { useFocusTrap } from '../a11y/useFocusTrap';
import {
  daysUntilDeadline,
  downloadCaseDataAsJsonFile,
  dsgvoRequestTypeBadgeClass,
  fulfillDeletionRequest,
  listDsgvoRequestsForCase,
  rejectDsgvoRequest,
} from '../../lib/supabaseDsgvo';
import { getSupabaseClient } from '../../lib/supabase';
import { DEMO_DSGVO_REQUESTS, isDemoCaseId } from '../../lib/demo/demoFixtures';
import type {
  Database,
  DsgvoRequestRow,
  DsgvoRequestStatus,
  DsgvoRequestType,
} from '../../lib/supabaseTypes';

interface DsgvoSectionProps {
  readonly caseId: string;
}

type Tab = 'open' | 'in_progress' | 'history';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: ReadonlyArray<DsgvoRequestRow> }
  | { kind: 'error'; message: string };

export function DsgvoSection({ caseId }: DsgvoSectionProps) {
  const isDemo = isDemoCaseId(caseId);
  const [tab, setTab] = useState<Tab>('open');
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    if (isDemo) {
      setLoad({ kind: 'ready', items: DEMO_DSGVO_REQUESTS });
      return;
    }
    try {
      const items = await listDsgvoRequestsForCase(caseId);
      setLoad({ kind: 'ready', items });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Konnte DSGVO-Anfragen nicht laden.',
      });
    }
  }, [caseId, isDemo]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (load.kind !== 'ready') return [];
    if (tab === 'open') {
      return load.items
        .filter((r) => r.status === 'open')
        .slice()
        .sort((a, b) => Date.parse(a.legal_deadline_at) - Date.parse(b.legal_deadline_at));
    }
    if (tab === 'in_progress') {
      return load.items
        .filter((r) => r.status === 'in_progress')
        .slice()
        .sort((a, b) => Date.parse(a.legal_deadline_at) - Date.parse(b.legal_deadline_at));
    }
    return load.items
      .filter((r) => r.status === 'fulfilled' || r.status === 'rejected' || r.status === 'expired')
      .slice()
      .sort((a, b) => {
        const dateA = a.handled_at ?? a.requested_at;
        const dateB = b.handled_at ?? b.requested_at;
        return Date.parse(dateB) - Date.parse(dateA);
      });
  }, [load, tab]);

  const counts = useMemo(() => {
    if (load.kind !== 'ready') return { open: 0, in_progress: 0, history: 0 };
    let open = 0;
    let inp = 0;
    let hist = 0;
    for (const r of load.items) {
      if (r.status === 'open') open += 1;
      else if (r.status === 'in_progress') inp += 1;
      else hist += 1;
    }
    return { open, in_progress: inp, history: hist };
  }, [load]);

  return (
    <section
      className="mb-10 rounded-sm border border-stone-200 bg-white"
      aria-labelledby="dsgvo-cockpit-title"
    >
      <div className="border-b border-stone-200 px-6 py-5 md:px-10">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.dsgvo.cockpit.sectionLabel}
        </p>
        <h2
          id="dsgvo-cockpit-title"
          className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900"
        >
          {de.dsgvo.cockpit.sectionTitle}
        </h2>
        <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-600">
          {de.dsgvo.cockpit.sectionIntro}
        </p>
      </div>

      <div
        className="flex flex-wrap items-center gap-1 border-b border-stone-200 px-3 py-2 md:px-7"
        role="tablist"
        aria-label="DSGVO-Filter"
      >
        <TabBtn active={tab === 'open'} onClick={() => setTab('open')}>
          {de.dsgvo.cockpit.tabOpen}
          <CountBadge count={counts.open} />
        </TabBtn>
        <TabBtn active={tab === 'in_progress'} onClick={() => setTab('in_progress')}>
          {de.dsgvo.cockpit.tabInProgress}
          <CountBadge count={counts.in_progress} />
        </TabBtn>
        <TabBtn active={tab === 'history'} onClick={() => setTab('history')}>
          {de.dsgvo.cockpit.tabHistory}
          <CountBadge count={counts.history} />
        </TabBtn>
      </div>

      <div className="px-6 py-6 md:px-10">
        {load.kind === 'loading' && (
          <p className="font-serif text-sm italic text-stone-500">Wird geladen …</p>
        )}
        {load.kind === 'error' && (
          <p
            role="alert"
            className="rounded-sm border border-red-200 bg-red-50 px-4 py-2 font-serif text-sm text-red-800"
          >
            {load.message}
          </p>
        )}
        {load.kind === 'ready' && filtered.length === 0 && (
          <p className="font-serif text-sm italic text-stone-500">
            {tab === 'history' ? de.dsgvo.cockpit.emptyHistory : de.dsgvo.cockpit.empty}
          </p>
        )}
        {load.kind === 'ready' && filtered.length > 0 && (
          <ul className="space-y-3">
            {filtered.map((r) => (
              <DsgvoRequestCard
                key={r.id}
                request={r}
                isDemo={isDemo}
                caseId={caseId}
                onChange={() => {
                  void refresh();
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Per-Request-Card
// ============================================================================

function DsgvoRequestCard({
  request,
  isDemo,
  caseId,
  onChange,
}: {
  request: DsgvoRequestRow;
  isDemo: boolean;
  caseId: string;
  onChange: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [downloadingExport, setDownloadingExport] = useState(false);

  const days = daysUntilDeadline(request.legal_deadline_at);
  const isExpired = days < 0;
  const isUrgent = !isExpired && days < 7;
  const isOpen = request.status === 'open' || request.status === 'in_progress';

  // ---- Aktionen -----------------------------------------------------------
  async function performGenericFulfill(): Promise<void> {
    if (isDemo) {
      onChange();
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const sb = getSupabaseClient();
      const { data: u } = await sb.auth.getUser();
      if (!u.user) throw new Error('Nicht eingeloggt');
      const update: Database['public']['Tables']['dsgvo_requests']['Update'] = {
        status: 'fulfilled',
        handled_by: u.user.id,
        handled_at: new Date().toISOString(),
      };
      const { error: e } = await sb
        .from('dsgvo_requests')
        .update(update as never)
        .eq('id', request.id)
        .in('status', ['open', 'in_progress']);
      if (e) throw e;
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.dsgvo.cockpit.actionFulfillError);
    } finally {
      setWorking(false);
    }
  }

  async function performDeletion(): Promise<void> {
    if (isDemo) {
      setConfirmDeleteOpen(false);
      onChange();
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await fulfillDeletionRequest(request.id);
      setConfirmDeleteOpen(false);
      onChange();
    } catch (err) {
      setConfirmDeleteOpen(false);
      setError(err instanceof Error ? err.message : de.dsgvo.cockpit.actionFulfillError);
    } finally {
      setWorking(false);
    }
  }

  async function performExportDownload(): Promise<void> {
    if (isDemo) {
      setError(null);
      return;
    }
    setDownloadingExport(true);
    setError(null);
    try {
      await downloadCaseDataAsJsonFile(caseId);
    } catch (err) {
      setError(err instanceof Error ? err.message : de.dsgvo.cockpit.actionFulfillError);
    } finally {
      setDownloadingExport(false);
    }
  }

  async function performReject(reason: string): Promise<void> {
    if (isDemo) {
      setRejectModalOpen(false);
      onChange();
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await rejectDsgvoRequest(request.id, reason);
      setRejectModalOpen(false);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.dsgvo.cockpit.actionRejectError);
    } finally {
      setWorking(false);
    }
  }

  return (
    <li className="rounded-sm border border-stone-200 bg-white">
      <div className="px-4 py-3 md:px-5 md:py-4">
        {/* Top row — Type + Status + Frist */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${dsgvoRequestTypeBadgeClass(
              request.request_type,
            )}`}
          >
            {de.dsgvo.requestTypes[request.request_type]}
          </span>
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${statusBadge(request.status)}`}
          >
            {de.dsgvo.requestStatuses[request.status]}
          </span>
          <span className="font-sans text-[10px] uppercase tracking-widest text-stone-500">
            {de.dsgvo.cockpit.requestedAt.replace(
              '{date}',
              new Date(request.requested_at).toLocaleString('de-DE'),
            )}
          </span>
        </div>

        {/* Frist-Countdown — prominent */}
        {isOpen && (
          <div
            className={`mt-2 inline-flex items-center gap-2 rounded-sm px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest ${
              isExpired
                ? 'bg-red-100 text-red-900'
                : isUrgent
                  ? 'bg-amber-100 text-amber-900'
                  : 'bg-stone-100 text-stone-700'
            }`}
          >
            {isExpired ? (
              <>
                <AlertTriangle size={12} aria-hidden="true" />
                {de.dsgvo.cockpit.deadlineExpired} · {de.dsgvo.cockpit.deadlineEscalation}
              </>
            ) : (
              de.dsgvo.cockpit.deadlineRemaining.replace('{days}', String(days))
            )}
          </div>
        )}

        {/* Begründung des Mandanten */}
        <div className="mt-3 rounded-sm border-l-2 border-stone-200 bg-stone-50 px-3 py-2">
          <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
            {de.dsgvo.cockpit.reasonLabel}
          </p>
          <p className="mt-1 font-serif text-sm text-stone-800">
            {request.reason && request.reason.trim().length > 0 ? (
              request.reason
            ) : (
              <span className="italic text-stone-500">{de.dsgvo.cockpit.reasonEmpty}</span>
            )}
          </p>
        </div>

        {/* Erledigt-Block */}
        {request.handled_at && (
          <div className="mt-2 rounded-sm border-l-2 border-emerald-300 bg-emerald-50 px-3 py-2">
            <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-emerald-900">
              {de.dsgvo.cockpit.handledAt.replace(
                '{date}',
                new Date(request.handled_at).toLocaleString('de-DE'),
              )}
            </p>
            {request.response_text && (
              <p className="mt-1 font-serif text-sm text-emerald-950">{request.response_text}</p>
            )}
          </div>
        )}

        {/* Action-Buttons je nach Type */}
        {isOpen && (
          <div className="mt-4 flex flex-wrap gap-2">
            {request.request_type === 'data_export' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    void performExportDownload();
                  }}
                  disabled={downloadingExport}
                  className="inline-flex min-h-[36px] items-center gap-2 rounded-sm border border-sky-700 bg-sky-50 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-sky-900 hover:bg-sky-100 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-2"
                >
                  <Download size={12} aria-hidden="true" />
                  {downloadingExport
                    ? de.dsgvo.cockpit.actionExportDownloading
                    : de.dsgvo.cockpit.actionExportDownload}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void performGenericFulfill();
                  }}
                  disabled={working}
                  className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-900 bg-stone-900 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                >
                  {working ? '…' : de.dsgvo.cockpit.actionExportFulfill}
                </button>
              </>
            )}
            {request.request_type === 'deletion' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDeleteOpen(true);
                  }}
                  disabled={working}
                  className="inline-flex min-h-[36px] items-center gap-2 rounded-sm bg-red-700 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-white hover:bg-red-800 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2"
                >
                  <Trash2 size={12} aria-hidden="true" />
                  {working
                    ? de.dsgvo.cockpit.actionDeleteRunning
                    : de.dsgvo.cockpit.actionDeleteConfirm}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRejectModalOpen(true);
                  }}
                  disabled={working}
                  className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-200 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                >
                  {de.dsgvo.cockpit.actionRejectOpen}
                </button>
              </>
            )}
            {request.request_type === 'consent_withdrawal' && (
              <button
                type="button"
                onClick={() => {
                  void performGenericFulfill();
                }}
                disabled={working}
                className="inline-flex min-h-[36px] items-center rounded-sm border border-amber-700 bg-amber-50 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-amber-900 hover:bg-amber-100 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700 focus-visible:ring-offset-2"
              >
                {working ? '…' : de.dsgvo.cockpit.actionWithdrawalAck}
              </button>
            )}
            {request.request_type === 'rectification' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    void performGenericFulfill();
                  }}
                  disabled={working}
                  className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-900 bg-stone-900 px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                >
                  {working ? '…' : de.dsgvo.cockpit.actionGenericFulfill}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRejectModalOpen(true);
                  }}
                  disabled={working}
                  className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-200 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                >
                  {de.dsgvo.cockpit.actionRejectOpen}
                </button>
              </>
            )}
          </div>
        )}

        {/* Hint für Datenexport */}
        {isOpen && request.request_type === 'data_export' && (
          <p className="mt-2 font-serif text-xs italic text-stone-600">
            {de.dsgvo.cockpit.actionExportHint}
          </p>
        )}

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 font-serif text-sm text-red-800"
          >
            {error}
          </p>
        )}
      </div>

      {confirmDeleteOpen && (
        <ConfirmDialog
          title={de.dsgvo.cockpit.actionDeleteConfirmTitle}
          body={de.dsgvo.cockpit.actionDeleteConfirmBody}
          confirmLabel={de.dsgvo.cockpit.actionDeleteConfirmYes}
          cancelLabel={de.dsgvo.cockpit.actionDeleteConfirmNo}
          dangerous
          submitting={working}
          onConfirm={() => {
            void performDeletion();
          }}
          onCancel={() => {
            setConfirmDeleteOpen(false);
          }}
        />
      )}

      {rejectModalOpen && (
        <RejectDialog
          submitting={working}
          requestType={request.request_type}
          onSubmit={(reason) => {
            void performReject(reason);
          }}
          onCancel={() => {
            setRejectModalOpen(false);
          }}
        />
      )}
    </li>
  );
}

// ============================================================================
// Reject-Dialog mit Pflicht-Begründung
// ============================================================================

function RejectDialog({
  submitting,
  requestType,
  onSubmit,
  onCancel,
}: {
  submitting: boolean;
  requestType: DsgvoRequestType;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { active: true });

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onCancel]);

  const example = requestType === 'deletion' ? de.dsgvo.cockpit.actionRejectExampleDeletionB2G : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dsgvo-reject-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-stone-200 px-5 py-4 md:px-6">
          <h3
            id="dsgvo-reject-title"
            className="font-serif text-xl font-bold tracking-tight text-stone-900"
          >
            {de.dsgvo.cockpit.actionRejectModalTitle}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label={de.dsgvo.cockpit.actionRejectCancel}
            className="rounded-sm p-1 text-stone-600 hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="px-5 py-5 md:px-6">
          <p className="font-serif text-sm leading-relaxed text-stone-700">
            {de.dsgvo.cockpit.actionRejectModalIntro}
          </p>
          {example.length > 0 && (
            <p className="mt-2 rounded-sm border border-stone-100 bg-stone-50 px-3 py-2 font-serif text-xs italic text-stone-600">
              {example}
            </p>
          )}
          <label
            htmlFor="dsgvo-reject-reason"
            className="mt-4 block font-sans text-xs font-medium uppercase tracking-widest text-stone-600"
          >
            {de.dsgvo.cockpit.actionRejectReasonLabel}
          </label>
          <textarea
            id="dsgvo-reject-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder={de.dsgvo.cockpit.actionRejectReasonPlaceholder}
            rows={6}
            maxLength={4000}
            className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          />
          <p className="mt-1 text-right font-mono text-[10px] text-stone-500">
            {de.dsgvo.cockpit.actionRejectReasonCharCount.replace('{count}', String(reason.length))}
          </p>
          <div className="mt-4 flex flex-col items-stretch justify-end gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.dsgvo.cockpit.actionRejectCancel}
            </button>
            <button
              type="button"
              disabled={submitting || reason.trim().length === 0}
              onClick={() => {
                onSubmit(reason);
              }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-red-700 px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2"
            >
              {submitting ? '…' : de.dsgvo.cockpit.actionRejectSubmit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Confirm-Dialog (Generic)
// ============================================================================

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  dangerous = false,
  submitting,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  dangerous?: boolean;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { active: true });

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dsgvo-confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        <div className="border-b border-stone-200 px-5 py-4 md:px-6">
          <h3
            id="dsgvo-confirm-title"
            className="font-serif text-xl font-bold tracking-tight text-stone-900"
          >
            {title}
          </h3>
        </div>
        <div className="px-5 py-4 md:px-6">
          <p className="font-serif text-sm leading-relaxed text-stone-700">{body}</p>
          <div className="mt-5 flex flex-col items-stretch justify-end gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={onConfirm}
              className={`inline-flex min-h-[44px] items-center justify-center rounded-sm px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                dangerous
                  ? 'bg-red-700 hover:bg-red-800 focus-visible:ring-red-700'
                  : 'bg-stone-900 hover:bg-stone-800 focus-visible:ring-stone-900'
              }`}
            >
              {submitting ? '…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex min-h-[44px] items-center rounded-sm px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 ${
        active
          ? 'bg-stone-900 text-white'
          : 'bg-white text-stone-700 hover:bg-stone-100 hover:text-stone-900'
      }`}
    >
      {children}
    </button>
  );
}

function CountBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span
      aria-label={`${String(count)} Einträge`}
      className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-stone-200 px-1.5 font-mono text-[10px] text-stone-800"
    >
      {count}
    </span>
  );
}

function statusBadge(status: DsgvoRequestStatus): string {
  switch (status) {
    case 'open':
      return 'bg-stone-100 text-stone-700';
    case 'in_progress':
      return 'bg-sky-100 text-sky-800';
    case 'fulfilled':
      return 'bg-emerald-100 text-emerald-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'expired':
      return 'bg-stone-200 text-stone-500';
  }
}
